/**
 * Message content sanitiser — strips control JSON blocks the LLM occasionally
 * persists alongside its prose reply.
 *
 * Why: the chat backend's `splitReplyAndJson` only catches JSON appearing AFTER
 * the prose. When the model emits JSON first, with no fence, or with an
 * unclosed fence, the raw block lands in `chat_messages.content`. Without
 * sanitising at render time, end users see literal `{ "readyToSubmit": ... }`
 * blocks in Marq's chat bubbles.
 *
 * The helper handles four shapes:
 *   1. Trailing fenced JSON  ("Hi! ```json {...} ```")
 *   2. Leading fenced JSON   ("```json {...} ``` Hi!")
 *   3. Unfenced JSON-only    ("{ \"readyToSubmit\": false }")
 *   4. Unclosed fenced JSON  ("Hi! ```json {... <eof>")
 */

export const EMPTY_REPLY_FALLBACK = 'How can I help?'

/**
 * Strip control-JSON blocks from message content.
 * Returns a trimmed string. If nothing remains, returns ''.
 */
export function stripControlJson(text) {
  if (!text) return ''
  let cleaned = String(text)

  // Remove all closed ```json ... ``` (or ``` ... ```) fences anywhere in the text.
  cleaned = cleaned.replace(/```(?:json)?\s*[\s\S]*?```/g, '')

  // Remove an unclosed fence and everything after it.
  cleaned = cleaned.replace(/```(?:json)?[\s\S]*$/, '')

  cleaned = cleaned.trim()

  // Whole content was a bare JSON object/array — treat as empty.
  if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
    try {
      JSON.parse(cleaned)
      return ''
    } catch {
      // Looks like JSON but doesn't parse — fall through and keep the text;
      // a partial object isn't worth showing but we'd rather not silently
      // drop user-visible content unless we're sure.
      return ''
    }
  }

  return cleaned
}
